import { Check, Copy } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { FundingPageShell } from '../../components/smart/FundingPageShell';
import { BarcodeView } from '../../components/smart/ui/BarcodeView';
import { Button } from '../../components/smart/ui/Button';
import { formatCurrency } from '../../components/smart/utils/format';
import { SMART_ROUTES } from '../../constants';
import { smartMockBarcode, smartMockPartner } from '../../gallery/smartMock';

const formatCountdown = (ms: number): string => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');
  return hours > 0
    ? `${hours}:${pad(minutes)}:${pad(seconds)}`
    : `${pad(minutes)}:${pad(seconds)}`;
};

export const BarcodePage = () => {
  const { t } = useTranslation('smart');
  const navigate = useNavigate();
  const instrument = smartMockBarcode;
  const [now, setNow] = useState(() => Date.now());
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const remainingMs = new Date(instrument.expiresAt).getTime() - now;
  const instructions = t('home.barcode.instructions', {
    returnObjects: true,
  }) as string[];

  return (
    <FundingPageShell linkId={smartMockPartner.linkId}>
        <section className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold tracking-tight">
            {t('home.barcode.title')}
          </h1>
          <p className="text-fg-muted text-sm">{t('home.barcode.subtitle')}</p>
        </section>

        <div className="border-border bg-bg-elevated mt-5 flex items-center justify-between rounded-2xl border p-4">
          <span className="text-fg-muted text-[11px] font-semibold uppercase tracking-wide">
            {t('home.barcode.amountLabel')}
          </span>
          <span className="text-fg text-xl font-semibold tabular-nums">
            {formatCurrency(instrument.amount, instrument.currency)}
          </span>
        </div>

        <div className="mt-4">
          <BarcodeView value={instrument.data} />
        </div>

        <p className="text-fg-muted mt-3 text-center text-xs">
          {t('home.barcode.expiresInCountdown', {
            time: formatCountdown(remainingMs),
          })}
        </p>

        <dl className="border-border bg-bg-elevated mt-4 flex flex-col gap-3 rounded-2xl border p-4 text-sm">
          <div className="flex items-center justify-between gap-2">
            <dt className="text-fg-muted text-[11px] font-semibold uppercase tracking-wide">
              {t('home.barcode.merchantLabel')}
            </dt>
            <dd className="text-fg font-semibold">{instrument.merchantName}</dd>
          </div>
          <div className="flex items-center justify-between gap-2">
            <dt className="text-fg-muted text-[11px] font-semibold uppercase tracking-wide">
              {t('home.barcode.referenceLabel')}
            </dt>
            <dd className="flex items-center gap-2">
              <span className="text-fg font-mono text-xs">{instrument.ref}</span>
              <button
                type="button"
                onClick={() => {
                  void navigator.clipboard.writeText(instrument.ref).then(() => {
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  });
                }}
                aria-label={t('home.barcode.copyReference')}
                className="text-fg-muted hover:text-fg transition-colors"
              >
                {copied ? (
                  <Check className="size-4" aria-hidden />
                ) : (
                  <Copy className="size-4" aria-hidden />
                )}
              </button>
            </dd>
          </div>
        </dl>

        <div className="mt-5">
          <h2 className="text-fg-muted text-[11px] font-semibold uppercase tracking-wide">
            {t('home.barcode.instructionsTitle')}
          </h2>
          <ol className="text-fg mt-2 flex flex-col gap-2 text-sm">
            {(Array.isArray(instructions) ? instructions : []).map((step, i) => (
              <li key={i} className="flex gap-2.5">
                <span className="bg-accent/10 text-accent flex size-5 shrink-0 items-center justify-center rounded-full text-[11px] font-bold tabular-nums">
                  {i + 1}
                </span>
                <span className="leading-snug">{step}</span>
              </li>
            ))}
          </ol>
        </div>

        <p className="text-fg-muted mt-5 text-center text-xs">
          {t('home.barcode.waiting')}
        </p>

        <div className="mt-3 flex flex-col gap-2">
          <Button
            variant="secondary"
            fullWidth
            onClick={() => navigate(SMART_ROUTES.MERCHANT_PICKER)}
          >
            {t('home.barcode.changeStore')}
          </Button>
          <Button
            variant="ghost"
            fullWidth
            onClick={() => navigate(SMART_ROUTES.HOME)}
          >
            {t('home.barcode.backHome')}
          </Button>
        </div>
    </FundingPageShell>
  );
};
